/* eslint-disable no-param-reassign */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { ResponsiveFunnel } from '@nivo/funnel';

import CustomLegend from '../../utils/chartTools/createLegend';
import colorSelector from '../../utils/formatCharts/handleColor';
import customTooltip from '../../utils/chartTools/handleCustomTooltip';
import { labelValues } from '../../utils/formatCharts/handleCustomValue';
import { genData1D } from '../../utils/formatCharts/handleData';
// import { checkErrors } from '../../utils/chartTools/kpiMsg';
import KpiStatus from '../../utils/chartTools/KpiStatus';
import CustomLabel from './CustomLabel';

const emptyObj = {};

const Funnel = ({
  data, styleConfig, isEdit, globalConfig = emptyObj, handleDrilldownClick = null,
}) => {
  const [chartData, setChartData] = useState({ ready: false });
  const [filteredChartData, setFilteredChartData] = useState([]);
  const [hiddenIds, setHiddenIds] = useState([]);

  useEffect(() => {
    setChartData(genData1D(data, styleConfig, true));
  }, [
    data,
    styleConfig.DataFormat,
    styleConfig.DataPercentFormat,
    styleConfig.DataFormatAlt,
    styleConfig.DataPercentFormatAlt,
    styleConfig.DataAbsoluteFormatAlt,
    styleConfig?.InvertVariation,
    ...styleConfig.CustomSortControl.kpiCategories,
    styleConfig.SortValuesControl,
  ]);

  useEffect(() => {
    if (chartData.ready) {
      setFilteredChartData(chartData.data.filter((item) => !hiddenIds.includes(item.id)));
    }
  }, [chartData, hiddenIds]);

  const createLabel = (label, x) => {
    const {
      EnableLabelMinControl,
      LabelMinPercentControl,
    } = styleConfig;
    if (
      EnableLabelMinControl
      && (label.value * 100) / chartData.total < (LabelMinPercentControl ?? 0)
    ) return null;
    let offset = 0;
    let val = '';
    const [valIni, valEnd] = labelValues(label.value ?? 0, label.value2 ?? 0, styleConfig);
    if (styleConfig.LabelLineBreak && styleConfig?.DataFormatAlt !== 'none') {
      val = (
        <>
          <tspan>{valIni}</tspan>
          <tspan x={x} dy="1.2em">{valEnd}</tspan>
        </>
      );
      offset = -10;
    } else val = `${valIni} ${valEnd}`.trim();
    return <tspan y={offset}>{val}</tspan>;
  };

  const createTooltip = (val) => customTooltip(val, 'funnel', styleConfig, handleDrilldownClick);

  return chartData.ready ? (
    <KpiStatus
      conditions={([
        [
          'NoLineSelected',
          data?.index?.[0] === 'Totais',
        ],
        [
          'HasNegativeValues',
          chartData.hasNegative,
        ],
      ])}
    >
      <ResponsiveFunnel
        data={filteredChartData}
        /* GENERAL */
        margin={styleConfig?.Margin || {
          top: 22,
          right: 180,
          bottom: 22,
          left: 180,
        }}
        onClick={handleDrilldownClick ? ((dataPoint) => {
          handleDrilldownClick(dataPoint?.data?.id);
        }) : null}
        shapeBlending={styleConfig?.BodyRadiusControl?.value}
        spacing={styleConfig?.CategorySpacingControl?.value}
        direction={styleConfig.LayoutControl ? styleConfig.LayoutControl : 'vertical'}
        interpolation={styleConfig.InterpolationControl ? styleConfig.InterpolationControl : 'smooth'}
        /* BORDER */
        borderWidth={styleConfig?.BorderWidthControl?.value}
        borderColor={
          styleConfig.BorderColorControl
            ? styleConfig.BorderColorControl
            : { from: 'color', modifiers: [['darker', 1.6]] }
        }
        /* STYLES/THEMES */
        colors={colorSelector({
          dataLength: chartData.data.length,
          config: styleConfig,
          colorKey: 'id',
          globalConfig,
        })}
        theme={{
          labels: {
            text: {
              fontSize: parseInt(styleConfig?.LabelsFontSizeControl?.value, 10),
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLabel : styleConfig?.LabelsFontFamilyControl?.value}', Arial`,
              transform: `translate(${styleConfig?.LabelTranslateX ? styleConfig?.LabelTranslateX : 0}px, ${styleConfig?.LabelTranslateY ? styleConfig?.LabelTranslateY : 0}px) rotate(${styleConfig?.LabelRotationControl ? styleConfig?.LabelRotationControl : 0}deg)`,
              transformOrigin: 'center center',
              transformBox: 'fill-box',
            },
          },
          legends: {
            text: {
              fontSize: parseInt(styleConfig?.LegendsFontSizeControl?.value, 10),
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLegend : styleConfig?.LegendsFontFamilyControl?.value}', Arial`,
              fill: styleConfig?.LegendFontColor ?? '#999',
              fontWeight: styleConfig?.LegendBold ? 'bold' : 'normal',
              fontStyle: styleConfig?.LegendItalic ? 'italic' : 'normal',
            },
            hidden: {
              text: {
                textDecoration: 'line-through',
              },
            },
          },
        }}
        /* LABELS */
        enableLabel={styleConfig?.EnableLabelControl?.checked}
        labelColor={styleConfig?.LabelTextColorControl}
        /* LEGEND */
        layers={[
          'separators',
          'parts',
          // 'labels',
          (dt) => (styleConfig.EnableLabelControl?.checked === false ? null : CustomLabel({
            data: dt.parts, styleConfig, globalConfig, content: createLabel,
          })),
          'annotations',
          (dt) => (
            styleConfig?.LegendControl?.checked ? (
              <CustomLegend
                chartDataAr={chartData.data}
                colorParts={dt.parts.reduce((aux, p) => {
                  aux[p.data.id] = p.color;
                  return aux;
                }, {})}
                sizes={{
                  width: dt.width,
                  height: dt.height,
                }}
                config={styleConfig}
                setToggleIds={setHiddenIds}
                isEdit={isEdit}
              />
            ) : null
          ),
        ]}
        /* TOOLTIPS */
        isInteractive={styleConfig?.InteractiveControl}
        tooltip={createTooltip}
        /* ETC */
        animate={styleConfig.InteractiveControl ? !!styleConfig.Animate : false}
        motionConfig={styleConfig.InteractiveControl ? (styleConfig.MotionConfig || 'default') : 'default'}
        currentPartSizeExtension={styleConfig.InteractiveControl && !!styleConfig.Animate
          ? (+styleConfig.PartExtension || 0) : 0}
        beforeSeparatorLength={20}
        afterSeparatorLength={20}
      />
    </KpiStatus>
  ) : <></>;
};

Funnel.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  styleConfig: PropTypes.objectOf(PropTypes.any).isRequired,
  isEdit: PropTypes.bool.isRequired,
  globalConfig: PropTypes.objectOf(PropTypes.any),
  handleDrilldownClick: PropTypes.func,
};

export default Funnel;
